import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router";

const NavItem = ({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}) => (
  <NavLink
    to={to}
    className="relative text-sm font-medium uppercase tracking-widest text-white/90 hover:text-white transition-colors group">
    {children}
    <span className="absolute -bottom-1 left-1/2 w-0 h-0.5 bg-white transition-all duration-300 ease-out group-hover:w-full group-hover:left-0" />
  </NavLink>
);

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    // Check initial scroll
    handleScroll();

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <nav
        className={cn(
          "fixed top-0 left-0 right-0 z-50 w-full transition-all duration-500",
          // Gradient overlay initially, solid/blurred background on scroll
          scrolled
            ? "bg-black/80 backdrop-blur-md py-4 shadow-sm"
            : "bg-linear-to-b from-black/60 to-transparent py-6",
        )}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="block">
            <img
              src="/logo-h.png"
              alt="Arihant Infracon"
              className="h-8 md:h-10 w-auto"
            />
          </Link>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center gap-10">
            <NavItem to="/">Home</NavItem>
            <NavItem to="/projects">Projects</NavItem>
            <NavItem to="/about">About Us</NavItem>
            <NavItem to="/contact">Contact</NavItem>
          </div>

          {/* CTA Button */}
          {/* <div className="hidden md:block">
            <button className="px-6 py-2 border border-white text-white text-sm font-medium uppercase tracking-wide transition-all duration-300 hover:bg-primary hover:border-primary">
              Get a Quote
            </button>
          </div> */}

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(true)}
            className="md:hidden text-white p-2">
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </nav>

      {/* Full Screen Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="fixed inset-0 z-60 bg-[#111111] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 px-6 md:px-12">
              <span className="text-xl font-bold text-white tracking-widest uppercase">
                ARIHANT INFRACON
              </span>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white p-2 hover:bg-white/10 rounded-full transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Links */}
            <div className="flex-1 flex flex-col justify-center items-center gap-8">
              {[
                { name: "Home", path: "/" },
                { name: "Expertise", path: "/services" },
                { name: "Projects", path: "/projects" },
                { name: "About Us", path: "/about" },
                { name: "Contact", path: "/contact" },
              ].map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className="text-3xl font-light text-white hover:text-primary transition-colors uppercase tracking-widest">
                  {link.name}
                </Link>
              ))}

              <button className="mt-8 px-8 py-3 border border-white text-white text-lg font-medium uppercase tracking-wide transition-all duration-300 active:bg-primary active:border-primary">
                Get a Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
